import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { KevelCampaignControl } from "web/react/components/kevel-campaign-control/kevel-campaign-control";
import { Button, ButtonProps } from "web/react/emo/button";
import { useLeadLinkClick } from "web/react/hooks/use-lead-link-click/use-lead-link-click";
import { useInStockProductPageContext } from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import withReduxProvider from "web/react/redux-provider";
import { openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import { canUseMembership } from "web/script/modules/utils";
import { ProductBuyOptionsSerializer } from "web/types/serializers";

export type AffiliateBuyButtonProps = {
    buyOption: ProductBuyOptionsSerializer;
    isComparison?: boolean;
    buttonProps?: Partial<ButtonProps>;
    contextType?: "comparison_option" | "shop_now";
};

function _AffiliateBuyButton({
    buyOption,
    isComparison = false,
    buttonProps,
    contextType = "shop_now",
}: AffiliateBuyButtonProps): React.ReactElement {
    const { retailer_slug: retailerSlug, promotions } = buyOption;
    const { product, activeProduct } = useInStockProductPageContext();

    const hasPromotion = promotions.length > 0;
    const dispatch = useDispatch();

    function afterOnClick(extraContent): void {
        if (!userProfiler.isLoggedIn() && canUseMembership()) {
            dispatch(
                openCustomerCaptureOverlay({
                    productId: product.product_id,
                    captureType: "signup_lead_save",
                    analyticsEventLabel: isComparison ? "comparison_option" : "is-product-shop-now",
                    ...extraContent,
                })
            );
        }
    }

    const onClick = useCallback(() => {
        analytics.event({
            category: "buy_area",
            action: "clicked",
            label: "buy-on-store-button",
        });
    }, []);

    const { onClick: onLeadLinkClick, leadHref } = useLeadLinkClick({
        href: buyOption.affiliate_url,
        productId: product.product_id,
        reason: isComparison ? "buy-on-store-alternative-option" : "buy-on-store-button",
        openInNewTab: true,
    });

    const buttonTitle = gettext("product.buy_area.buy_now");

    const propsForButton = {
        title: buttonTitle,
        variant: "primary" as const,
        width: "full" as const,
        onClick: onClick,
        ...buttonProps,
        dataTestId: "affiliate-buy-button",
    };

    return (
        <KevelCampaignControl
            contextType={isComparison ? "comparison_option" : contextType}
            options={{
                action: "shop_now_button_click",
                hasPromotion,
                isAffiliate: true,
                retailerSlug,
            }}
            defaultAction={(event) => onLeadLinkClick(event)}
            analyticsEventLabel={isComparison ? "comparison_option" : "is-product-shop-now"}
            afterOnClick={afterOnClick}
            productImageUrl={activeProduct.productImageUrl}
            continueOptionHref={leadHref}
        >
            <a data-testid="buy-button" href={leadHref} target={"_blank"} rel="noreferrer">
                <Button {...propsForButton} />
            </a>
        </KevelCampaignControl>
    );
}

export const AffiliateBuyButton = withReduxProvider(_AffiliateBuyButton);
